import React from 'react';
import { FaExclamationTriangle, FaShieldAlt, FaBook, FaBalanceScale, FaUserCheck, FaGlobe, FaFileContract, FaHandPaper } from 'react-icons/fa';

const TermsPage = () => {
  const sections = [
    {
      icon: <FaBook className="text-blue-500" />,
      title: "General Information",
      content: "DOLPHIN is a decentralized, blockchain-based ecosystem utilizing smart contracts deployed on public blockchain networks. The platform is designed to provide educational content, digital utilities, and decentralized interaction mechanisms. All information provided through the DOLPHIN ecosystem—including interfaces, documentation, learning materials, and smart contract functionality—is offered strictly for general informational and educational purposes only."
    },
    {
      icon: <FaExclamationTriangle className="text-amber-500" />,
      title: "No Financial or Investment Advice",
      content: "Nothing within the DOLPHIN ecosystem constitutes financial, investment, legal, accounting, or trading advice. No communication, documentation, user interface display, or smart contract behavior should be interpreted as a recommendation, solicitation, or offer to invest, trade, or engage in any financial activity. Users are solely responsible for conducting independent research and, where appropriate, seeking advice from qualified professionals before participating."
    },
    {
      icon: <FaShieldAlt className="text-purple-500" />,
      title: "No Guarantee of Returns",
      content: "Participation in the DOLPHIN ecosystem does not guarantee profits, income, asset appreciation, or any form of financial return. Digital assets and blockchain-based systems are inherently volatile and speculative. Any outcomes experienced by users depend on individual actions, network conditions, market dynamics, and external factors beyond the platform's control."
    },
    {
      icon: <FaBalanceScale className="text-red-500" />,
      title: "Risk Acknowledgement",
      content: "By interacting with the platform, users acknowledge and accept the risks associated with blockchain technology.",
      risks: [
        "Smart contract vulnerabilities or failures",
        "Network congestion, delays, or protocol forks",
        "Loss of private keys, seed phrases, or access credentials",
        "Regulatory or legal changes in applicable jurisdictions",
        "Market volatility and liquidity risks"
      ],
      note: "Blockchain transactions are irreversible, and errors, losses, or misconfigurations may result in permanent loss of digital assets."
    },
    {
      icon: <FaUserCheck className="text-green-500" />,
      title: "Decentralization and User Responsibility",
      content: "DOLPHIN operates in a decentralized manner. Smart contracts execute automatically based on their programmed logic. No centralized authority controls user funds, transactions, or outcomes once interactions are initiated. Users retain full custody and responsibility for their wallets, private keys, and transaction approvals. The platform cannot reverse, modify, or recover blockchain transactions."
    },
    {
      icon: <FaGlobe className="text-cyan-500" />,
      title: "Regulatory Compliance",
      content: "Users are solely responsible for ensuring that their participation complies with applicable laws, regulations, and tax obligations in their jurisdiction. The platform makes no representations regarding legality or regulatory compliance in any specific country or region. Participation may be restricted or prohibited under certain regulatory frameworks."
    },
    {
      icon: <FaBook className="text-indigo-500" />,
      title: "Educational Materials",
      content: "Any educational content, learning PDFs, or informational resources provided within the ecosystem are intended solely for general knowledge and awareness. They do not constitute professional training, certification, or formal qualification and should not be relied upon as the sole basis for decision-making."
    },
    {
      icon: <FaFileContract className="text-gray-600" />,
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by applicable law, the platform, developers, contributors, and associated parties shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from or related to the use of the platform. This includes, but is not limited to, loss of funds, data, access, or opportunities."
    },
    {
      icon: <FaHandPaper className="text-blue-600" />,
      title: "Acceptance of Terms",
      content: "By accessing, interacting with, or using any component of the DOLPHIN ecosystem, users acknowledge that they have read, understood, and agreed to this disclaimer in full. Continued use of the platform constitutes ongoing acceptance of these terms."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4 sm:py-12 md:py-16">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <FaBalanceScale className="text-2xl text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Legal Disclaimer & Risk Disclosure
          </h1>
          <p className="text-gray-600 text-lg md:text-xl">
            Important information regarding the use of DOLPHIN Ecosystem
          </p>
          <div className="mt-6 inline-flex items-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
            <FaExclamationTriangle className="text-amber-500 mr-2" />
            <span className="text-amber-700 font-medium text-sm">
              Please read carefully before proceeding
            </span>
          </div>
        </div>

        {/* Content Container */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Scrollable Content */}
          <div className="h-[70vh] overflow-y-auto p-6 md:p-8">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <div 
                  key={index}
                  className="pb-8 border-b border-gray-100 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 flex items-center justify-center shadow-sm">
                      {section.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">
                            {index + 1}
                          </span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                          {section.title}
                        </h3>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed text-base md:text-lg">
                        {section.content}
                      </p>
                      
                      {section.risks && (
                        <div className="mt-4 space-y-2">
                          <ul className="space-y-3">
                            {section.risks.map((risk, riskIndex) => (
                              <li key={riskIndex} className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg flex items-center justify-center mr-3 mt-1">
                                  <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-pink-500 rounded-full"></div>
                                </div>
                                <span className="text-gray-700 text-base">
                                  {risk}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {section.note && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                              <p className="text-amber-800 font-medium text-base">
                                {section.note}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Last Updated
                </h4>
                <p className="text-gray-600">
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-0.5">
                <div className="bg-white rounded-xl px-6 py-3">
                  <p className="text-gray-700 font-medium text-center">
                    By using this platform, you agree to these terms
                  </p>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap gap-4 justify-center">
              {[
                { icon: '🔒', label: 'Secure' },
                { icon: '⚖️', label: 'Legal' },
                { icon: '📚', label: 'Educational' },
                { icon: '🌐', label: 'Decentralized' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center space-x-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                  <span>{item.icon}</span>
                  <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
          >
            ← Back to Platform
          </button>
          <p className="mt-4 text-gray-500 text-sm">
            For questions, contact: legal@dolphinecosystem.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;